import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import api from "core/interceptors/api";

const roleOptions = [
  "Admin",
  "Telecaller",
  "Marketing Manager",
];

export default function IncentiveAddModal({ open, handleClose }) {
  const [input, setInput] = useState({
    percentage: "",
    description: "",
    status: "active",
    role: "", // New role field
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setInput({
        percentage: "",
        description: "",
        status: "active",
        role: "", // Reset role
      });
      setErrors({});
    }
  }, [open]);

  const validateField = (name, value) => {
    let error = "";
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "percentage":
        const parsedPercentage = parseFloat(value);
        if (isNaN(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100) {
          error = "Percentage must be between 0 and 100.";
        }
        break;
      case "description":
        if (!trimmedValue) {
          error = "Description is required.";
        } else if (trimmedValue.length < 10) {
          error = "Description must be at least 10 characters long.";
        }
        break;
      case "status":
        if (!trimmedValue) {
          error = "Status is required.";
        }
        break;
      case "role":
        if (!trimmedValue) {
          error = "Role is required.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === "percentage" ? (value === "" ? "" : parseFloat(value)) : value;
    setInput((prev) => ({ ...prev, [name]: processedValue }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, processedValue) }));
  };

  const validateForm = () => {
    const fields = ["percentage", "description", "status", "role"];
    const newErrors = {};
    let isValid = true;

    fields.forEach((field) => {
      const error = validateField(field, input[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) {
    Swal.fire("Validation Error", "Please correct the errors in the form.", "error");
    return;
  }

  setLoading(true);
  try {
    // Check for existing incentive for the same role
    const existing = await api.get(`/incentives?role=${encodeURIComponent(input.role)}`);

   if (existing.data?.incentives?.length > 0){
   handleClose(); 
      Swal.fire(
        "Duplicate Incentive",
        `An incentive already exists for the role "${input.role}".`,
        "warning"
      );
      setLoading(false);
      return;
    }

    // Proceed with creation if no duplicate found
    const payload = {
      ...input,
      percentage: parseFloat(input.percentage),
    };

    const response = await api.post("/incentives", payload);
handleClose();
    if (response.data.message === "Incentive created successfully") {
      Swal.fire("Success", response.data.message, "success");
    } else {
      Swal.fire("Error", response.data.message || "Failed to add incentive.", "error");
    }
  } catch (err) {
    console.error("Error adding incentive:", err.response || err);
    Swal.fire("Error", "An unexpected error occurred while adding incentive.", "error");
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add New Incentive</DialogTitle>
        <DialogContent dividers>
          <MDBox pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Percentage"
                  name="percentage"
                  value={input.percentage}
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  margin="dense"
                  required
                  error={!!errors.percentage}
                  helperText={errors.percentage}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={input.description}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={3}
                  required
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              <Grid item xs={12}>
              <TextField
  select
  label="Role"
  name="role"
  value={input.role}
  onChange={handleChange}
  fullWidth
  margin="dense"
  required
  error={!!errors.role}
  helperText={errors.role}
  size="medium" // explicitly set medium size
  SelectProps={{
    sx: {
      minHeight: 44, // increase minHeight for the select dropdown display area
      paddingTop: "12px",
      paddingBottom: "12px",
    }
  }}
  sx={{ // to increase overall box height
    "& .MuiInputBase-root": {
      minHeight: 44,
      alignItems: "center",
    },
  }}
>
  {roleOptions.map((role) => (
    <MenuItem key={role} value={role}>
      {role}
    </MenuItem>
  ))}
</TextField>

              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary" disabled={loading}>
            Cancel
          </MDButton>
          <MDButton type="submit" color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Save"}
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
